/**
 * View Service - API client functions for MyVault views
 *
 * Provides typed API functions for view data operations.
 * Designed to be used with React Query hooks.
 *
 * Note: This service uses a factory pattern that accepts authFetch
 * to allow integration with the useAuthFetch hook.
 */

import type {
  ViewDefinition,
  ViewRow,
  ViewFilter,
  FilterOperator,
  ViewCreate,
  ViewCreateFull,
  ViewUpdate,
} from "../types";

// API endpoints
const VIEWS_API_BASE = "/api/workspace/myvault/views";
const VIEW_ROWS_API_BASE = "/api/workspace/myvault/view-rows";

export interface CanvasWriteContext {
  canvasId: string;
  elementId: string;
  embeddingPath?: Array<{ canvasId: string; elementId: string }>;
}

export function withEmbeddingPath(
  url: string,
  embeddingPath?: CanvasWriteContext["embeddingPath"],
): string {
  if (!embeddingPath?.length) return url;
  const params = new URLSearchParams();
  for (const host of embeddingPath) {
    params.append("embedding_path", `${host.canvasId}:${host.elementId}`);
  }
  return `${url}${url.includes("?") ? "&" : "?"}${params.toString()}`;
}

export function withCanvasWriteContext(
  url: string,
  context?: CanvasWriteContext,
): string {
  if (!context) return url;
  const params = new URLSearchParams({
    canvas_id: context.canvasId,
    element_id: context.elementId,
  });
  return withEmbeddingPath(
    `${url}${url.includes("?") ? "&" : "?"}${params.toString()}`,
    context.embeddingPath,
  );
}

/** Return a stored row identity; display-only grid keys are never write IDs. */
export function getViewRowId(row: Record<string, unknown>): string | null {
  for (const value of [row._rowId, row.__row_id, row.id]) {
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number" && Number.isFinite(value))
      return String(value);
  }
  return null;
}

/**
 * Type for the authFetch function from useAuthFetch hook
 */
export type AuthFetchFn = (
  endpoint: string,
  tag: string,
  options?: RequestInit,
  timeout?: number,
) => Promise<any>;

/**
 * Query parameters for view data requests
 */
export interface ViewQueryParams {
  canvasId?: string;
  page?: number;
  pageSize?: number;
  filters?: Array<
    Partial<ViewFilter> & {
      field: string;
      operator: FilterOperator;
      value?: unknown;
    }
  >;
  filterLogic?: "AND" | "OR";
  filterGroups?: Array<{
    logic: "AND" | "OR";
    filters: Array<
      Partial<ViewFilter> & {
        field: string;
        operator: FilterOperator;
        value?: unknown;
      }
    >;
  }>;
  sort?: { field: string; direction: "ASC" | "DESC" }[];
}

function withFilterOperator(
  filter: NonNullable<ViewQueryParams["filters"]>[number],
) {
  return {
    ...filter,
    operator: filter.operator ?? (Array.isArray(filter.value) ? "in" : "eq"),
  };
}

/**
 * Column metadata returned from view query
 */
export interface ViewColumn {
  field: string;
  headerName: string;
  type: "string" | "number" | "boolean" | "date" | "dateTime";
  editable: boolean;
  width?: number;
  description?: string;
  // Edit mode
  editMode?: "DIRECT" | "REFERENCE" | "READONLY";
  fkInfo?: {
    table: string;
    column: string;
    refTable: string;
    refColumn: string;
  };
  // Enhanced metadata for specialized editors
  inputType?:
    | "text"
    | "number"
    | "date"
    | "datetime"
    | "boolean"
    | "select"
    | "textarea"
    | "json"
    | "file";
  enumOptions?: string[];
  fkReferenceTableId?: string;
  fkReferenceTableName?: string;
  fkDisplayColumn?: string;
  formatHint?: string;
  minValue?: number;
  maxValue?: number;
  nullable?: boolean;
  // Combined link fields
  linkFieldIds?: string[];
  fkColumnKey?: string;
  actualTableName?: string;
}

/**
 * Processed view data with columns and rows
 */
export interface ProcessedViewData {
  columns: ViewColumn[];
  rows: Array<ViewRow & { _rowId?: string }>;
  totalCount: number;
}

export interface RelatedRecordsData extends ProcessedViewData {
  page: number;
  pageSize: number;
  hasMore: boolean;
}

function processViewResponse(response: any): ProcessedViewData {
  // Handle different response formats
  const rawRows = response.rows || response.data || [];
  const rawColumns = response.columns || [];

  const columns: ViewColumn[] = rawColumns.map((col: any) => {
    const tableName = col.tableName || col.table_name || "";
    const columnName =
      col.columnName ||
      col.column_name ||
      formatColumnName(col.name || col.field || "");
    const apiDisplayName = col.displayName || col.display_name;
    const displayName = apiDisplayName || `${tableName} ${columnName}`.trim();
    // The View field's display label is the header; the column name is the fallback.
    const headerLabel: string = apiDisplayName || columnName;

    return {
      field: col.name || col.field,
      headerName:
        headerLabel.length > 22
          ? headerLabel.substring(0, 19) + "..."
          : headerLabel,
      type: mapDataType(col.type || col.data_type || "string"),
      editable: col.editable ?? false,
      width:
        positiveWidth(col.width) ??
        positiveWidth(col.widthHint ?? col.width_hint),
      description: displayName,
      editMode: col.editMode || col.edit_mode,
      fkInfo: col.fkInfo || col.fk_info,
      inputType: col.inputType || col.input_type || "text",
      enumOptions: col.enumOptions || col.enum_options,
      fkReferenceTableId: col.fkReferenceTableId || col.fk_reference_table_id,
      fkReferenceTableName:
        col.fkReferenceTableName || col.fk_reference_table_name,
      fkDisplayColumn: col.fkDisplayColumn || col.fk_display_column,
      formatHint: col.formatHint || col.format_hint,
      minValue: col.minValue || col.min_value,
      maxValue: col.maxValue || col.max_value,
      nullable: col.nullable,
      linkFieldIds: col.linkFieldIds || col.link_field_ids,
      fkColumnKey: col.fkColumnKey || col.fk_column_key,
      actualTableName: col.actualTableName || col.actual_table_name,
    };
  });

  const rows = rawRows.map((row: any) => ({
    ...row,
    _rowId: getViewRowId(row) ?? undefined,
  }));

  return {
    columns,
    rows,
    totalCount: response.total_count ?? response.total ?? 0,
  };
}

/**
 * Helper to create headers with workspace ID
 */
const getWorkspaceHeaders = (workspaceId: string): Record<string, string> => ({
  "x-workspace-id": workspaceId,
});

/**
 * Map backend data type to frontend grid type
 */
function mapDataType(
  backendType: string,
): "string" | "number" | "boolean" | "date" | "dateTime" {
  const lowerType = backendType.toLowerCase();
  if (
    lowerType.includes("int") ||
    lowerType.includes("float") ||
    lowerType.includes("numeric") ||
    lowerType.includes("decimal")
  ) {
    return "number";
  }
  if (lowerType.includes("bool")) {
    return "boolean";
  }
  if (lowerType.includes("timestamp") || lowerType.includes("datetime")) {
    return "dateTime";
  }
  if (lowerType.includes("date")) {
    return "date";
  }
  return "string";
}

/**
 * Format column name for display
 */
function formatColumnName(name: string): string {
  const columnName = name.includes(".") ? name.split(".").pop()! : name;
  return columnName
    .replace(/[_-]/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/** A usable pixel width, or undefined so the grid falls back to its default. */
function positiveWidth(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : undefined;
}

/**
 * Create view service functions bound to a specific authFetch and workspaceId
 */
export function createViewService(authFetch: AuthFetchFn, workspaceId: string) {
  const headers = getWorkspaceHeaders(workspaceId);

  return {
    /**
     * Get all views for the workspace
     */
    async getViews(): Promise<ViewDefinition[]> {
      const response = await authFetch(VIEWS_API_BASE, "fetch views", {
        headers,
      });
      return response.items || response || [];
    },

    /**
     * Get a single view definition by ID (full with fields, joins, filters)
     */
    async getView(viewId: string | number): Promise<ViewDefinition> {
      const response = await authFetch(
        `${VIEWS_API_BASE}/${viewId}/full`,
        "fetch view",
        { headers },
      );
      return response;
    },

    /**
     * Query view data with pagination, filtering, and sorting
     */
    async queryViewData(
      viewId: string | number,
      params?: ViewQueryParams,
    ): Promise<ProcessedViewData> {
      const body = JSON.stringify({
        // Stored canvas controls may omit the operator. The query API requires
        // it for every condition, including those inside filter groups.
        filters: params?.filters?.map(withFilterOperator),
        filterLogic: params?.filterLogic,
        filterGroups: params?.filterGroups?.map((group) => ({
          ...group,
          filters: group.filters.map(withFilterOperator),
        })),
        sort: params?.sort,
        page: params?.page ?? 1,
        page_size: params?.pageSize ?? 25,
        include_total: true,
      });
      const response = await authFetch(
        `/api/workspace/myvault/view-rows/${viewId}/query${params?.canvasId ? `?canvas_id=${encodeURIComponent(params.canvasId)}` : ""}`,
        "fetch view data",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...headers },
          body,
        },
      );

      // Transform response to processed format

      return processViewResponse(response);
    },

    /** The server resolves the relationship and both View scopes. */
    async queryRelatedRecords(
      canvasId: string,
      elementId: string,
      rowId: string,
      page = 1,
    ): Promise<RelatedRecordsData> {
      const response = await authFetch(
        `/api/workspace/myvault/canvases/${encodeURIComponent(canvasId)}/elements/${encodeURIComponent(elementId)}/related-records`,
        "fetch related records",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...headers },
          body: JSON.stringify({ row_id: rowId, page }),
        },
      );
      return {
        ...processViewResponse(response),
        page: response.page,
        pageSize: response.page_size,
        hasMore: response.has_more === true,
      };
    },

    /**
     * Create a new row in a view's focus table
     */
    async createRow(
      viewId: string | number,
      data: Record<string, unknown>,
      context?: CanvasWriteContext,
    ): Promise<Record<string, unknown>> {
      const response = await authFetch(
        withCanvasWriteContext(`${VIEW_ROWS_API_BASE}/${viewId}/rows`, context),
        "create row",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...headers },
          body: JSON.stringify(data),
        },
      );
      return response;
    },

    /**
     * Update a single row in a view
     */
    async updateRow(
      viewId: string | number,
      rowId: string | number,
      data: Record<string, unknown>,
      context?: CanvasWriteContext,
    ): Promise<Record<string, unknown>> {
      const response = await authFetch(
        withCanvasWriteContext(
          `${VIEW_ROWS_API_BASE}/${viewId}/rows/${rowId}`,
          context,
        ),
        "update row",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...headers },
          body: JSON.stringify(data),
        },
      );
      return response;
    },

    /**
     * Delete a single row from a view's focus table
     */
    async deleteRow(
      viewId: string | number,
      rowId: string | number,
      context?: CanvasWriteContext,
    ): Promise<void> {
      await authFetch(
        withCanvasWriteContext(
          `${VIEW_ROWS_API_BASE}/${viewId}/rows/${rowId}`,
          context,
        ),
        "delete row",
        { method: "DELETE", headers },
      );
    },

    /**
     * Export view data to CSV
     */
    async exportView(
      viewId: string | number,
      filters?: ViewFilter[],
    ): Promise<Blob> {
      const response = await authFetch(
        `${VIEW_ROWS_API_BASE}/${viewId}/export`,
        "export view",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...headers },
          body: JSON.stringify({ filters }),
        },
      );
      // If response is already a blob, return it
      if (response instanceof Blob) {
        return response;
      }
      // A raw-configured authFetch hands back the Response itself. Read its
      // body rather than serialising the object: JSON.stringify on a Response
      // yields {"size":0,"timeout":0}, which the fallback below would then
      // deliver to the user as a .csv file containing that text.
      if (response instanceof Response) {
        return await response.blob();
      }
      // Otherwise create a blob from the response
      return new Blob([JSON.stringify(response)], { type: "text/csv" });
    },

    // =========================================================================
    // View Definition CRUD
    // =========================================================================

    /**
     * Create a new view definition
     */
    async createView(data: ViewCreate): Promise<ViewDefinition> {
      return authFetch(VIEWS_API_BASE, "create view", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(data),
      });
    },

    /**
     * Create a new view with all nested data (fields, joins, permissions)
     */
    async createViewFull(data: ViewCreateFull): Promise<ViewDefinition> {
      return authFetch(`${VIEWS_API_BASE}/full`, "create view full", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(data),
      });
    },

    /**
     * Update an existing view definition
     */
    async updateView(
      viewId: string | number,
      data: ViewUpdate,
    ): Promise<ViewDefinition> {
      return authFetch(`${VIEWS_API_BASE}/${viewId}`, "update view", {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete a view definition
     */
    async deleteView(viewId: string | number): Promise<void> {
      await authFetch(`${VIEWS_API_BASE}/${viewId}`, "delete view", {
        method: "DELETE",
        headers,
      });
    },

    // =========================================================================
    // Bulk Operations
    // =========================================================================

    /**
     * Bulk create rows in a view's focus table
     */
    async bulkCreateRows(
      viewId: string | number,
      rows: Record<string, unknown>[],
    ): Promise<BulkOperationResult> {
      return authFetch(
        `${VIEW_ROWS_API_BASE}/${viewId}/rows/bulk-create`,
        "bulk create rows",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...headers },
          body: JSON.stringify({ rows }),
        },
      );
    },

    /**
     * Bulk delete rows from a view's focus table
     */
    async bulkDeleteRows(
      viewId: string | number,
      rowIds: string[],
    ): Promise<BulkOperationResult> {
      return authFetch(
        `${VIEW_ROWS_API_BASE}/${viewId}/rows/bulk-delete`,
        "bulk delete rows",
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json", ...headers },
          body: JSON.stringify({ row_ids: rowIds }),
        },
      );
    },

    /**
     * Import CSV file into a view's focus table
     */
    async importCsv(
      viewId: string | number,
      file: File,
    ): Promise<BulkOperationResult> {
      const formData = new FormData();
      formData.append("file", file);
      return authFetch(`${VIEW_ROWS_API_BASE}/${viewId}/import`, "import csv", {
        method: "POST",
        headers: { ...headers },
        body: formData,
      });
    },
  };
}

/**
 * Result from bulk operations (create, update, delete, import)
 */
export interface BulkOperationResult {
  success_count: number;
  error_count: number;
  errors: Array<{ index?: number; row_id?: string; error: string }>;
}

/**
 * Export individual functions for direct use (requires passing authFetch and workspaceId)
 */
export async function getViews(
  authFetch: AuthFetchFn,
  workspaceId: string,
): Promise<ViewDefinition[]> {
  return createViewService(authFetch, workspaceId).getViews();
}

export async function getView(
  authFetch: AuthFetchFn,
  workspaceId: string,
  viewId: string | number,
): Promise<ViewDefinition> {
  return createViewService(authFetch, workspaceId).getView(viewId);
}

export async function queryViewData(
  authFetch: AuthFetchFn,
  workspaceId: string,
  viewId: string | number,
  params?: ViewQueryParams,
): Promise<ProcessedViewData> {
  return createViewService(authFetch, workspaceId).queryViewData(
    viewId,
    params,
  );
}

export async function createRow(
  authFetch: AuthFetchFn,
  workspaceId: string,
  viewId: string | number,
  data: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return createViewService(authFetch, workspaceId).createRow(viewId, data);
}

export async function updateRow(
  authFetch: AuthFetchFn,
  workspaceId: string,
  viewId: string | number,
  rowId: string | number,
  data: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return createViewService(authFetch, workspaceId).updateRow(
    viewId,
    rowId,
    data,
  );
}

export async function deleteRow(
  authFetch: AuthFetchFn,
  workspaceId: string,
  viewId: string | number,
  rowId: string | number,
): Promise<void> {
  return createViewService(authFetch, workspaceId).deleteRow(viewId, rowId);
}

export async function exportView(
  authFetch: AuthFetchFn,
  workspaceId: string,
  viewId: string | number,
  filters?: ViewFilter[],
): Promise<Blob> {
  return createViewService(authFetch, workspaceId).exportView(viewId, filters);
}

export async function createView(
  authFetch: AuthFetchFn,
  workspaceId: string,
  data: ViewCreate,
): Promise<ViewDefinition> {
  return createViewService(authFetch, workspaceId).createView(data);
}

export async function createViewFull(
  authFetch: AuthFetchFn,
  workspaceId: string,
  data: ViewCreateFull,
): Promise<ViewDefinition> {
  return createViewService(authFetch, workspaceId).createViewFull(data);
}

export async function updateView(
  authFetch: AuthFetchFn,
  workspaceId: string,
  viewId: string | number,
  data: ViewUpdate,
): Promise<ViewDefinition> {
  return createViewService(authFetch, workspaceId).updateView(viewId, data);
}

export async function deleteView(
  authFetch: AuthFetchFn,
  workspaceId: string,
  viewId: string | number,
): Promise<void> {
  return createViewService(authFetch, workspaceId).deleteView(viewId);
}
